import { FC, MouseEventHandler, useMemo, useState } from "react";
import { ColumnFiltersState } from "@tanstack/react-table";
import { Badge } from "../../../../Badge";
import { Button } from "../../../../Button";
import { FeaturedIcon } from "../../../../MiscIcons";
import { ProgressButton } from "../../../../ProgressButton";
import { SectionFooter } from "../../../../SectionFooter";
import { SideoutMenu, SideoutMenuHeader } from "../../../../SideoutMenu";
import { useTableContext } from "../../../context";
import { useFiltersInfo } from "../utils";
import { AddFilter } from "./AddFilter";
import { FilterRow } from "./FilterRow";

import { ReactComponent as FiltersIcon } from "./icons/filter.svg";

import styles from "./styles.module.scss";

export const AdvancedFilters: FC = (() => {
  const table = useTableContext();
  const { availableColumns, usedFiltersCount } = useFiltersInfo();

  const [ isOpen, setIsOpen ] = useState(false);
  const [ columnFilters, setColumnFilters ] = useState<ColumnFiltersState>(table?.getState().columnFilters || []);
  const [ columnOperators, setColumnOperators ] = useState<ColumnFiltersState>(availableColumns.map(c => ({
    id: c.id,
    value: c.columnDef.filterFn,
  })) || []);
  const unusedColumns = useMemo(
    () => availableColumns
      .filter(c => !columnFilters?.some(x => x.id === c.id)),
    [ availableColumns, columnFilters ],
  );

  const onRequestClose = () => {
    restore();
    setIsOpen(false);
  };

  const apply = () => {
    table?.setColumnFilters(columnFilters.filter(x => x.value !== undefined));
    columnOperators.filter(x => !!x.value)
      .forEach(o => {
        table!.getColumn(o.id)!.columnDef.filterFn = o.value as any;
      });

    setTimeout(onRequestClose, 0);
  };

  const restore = () => {
    setColumnFilters(table?.getState().columnFilters || []);
    setColumnOperators(availableColumns.map(c => ({
      id: c.id,
      value: c.columnDef.filterFn,
    })) || []);
  };

  const reset: MouseEventHandler = (event) => {
    const removeAll = event.getModifierState("Shift")
      .valueOf();

    setColumnFilters(removeAll ? [] : table?.initialState?.columnFilters!);
    setColumnOperators(availableColumns.map(c => ({
      id: c.id,
      value: c.columnDef.meta!.defaultFilterFn,
    })) || []);
  };


  if (!availableColumns.length) {
    return null;
  }

  return (
    <>
      <Button
        buttonSize="md"
        buttonType={usedFiltersCount ? "secondaryColor" : "secondaryGray"}
        onClick={() => setIsOpen(true)}
      >
        <FiltersIcon />
        <span className={styles.text}>Filters</span>
        {
          !!usedFiltersCount &&
          <Badge
            badgeSize="sm"
            badgeType="badgeColor"
            badgeColor="brand"
            className={styles.badge}
          >
            {usedFiltersCount}
          </Badge>
        }
      </Button>
      <SideoutMenu
        isOpen={isOpen}
        onRequestClose={onRequestClose}
        renderType="block"
        overlayProps={{ backgroundType: "gradient" }}
        scrollable
        shouldCloseOnEsc
        shouldCloseOnOverlayClick
        className={styles.sidebar}
        sidebarContainerClassName={styles.sidebarContainer}
        header={
          <SideoutMenuHeader
            text="Filters"
            supportingText="Apply filters to table data"
            onRequestClose={onRequestClose}
            icon={<FeaturedIcon size="md" type="modern"><FiltersIcon /></FeaturedIcon>}
          />
        }
        footer={
          <SectionFooter
            divider
            children={
              <ProgressButton
                buttonSize="md"
                buttonType="tertiaryColor"
                onClick={reset}
              >
                Reset
              </ProgressButton>
            }
            actions={
              <>
                <Button
                  buttonSize="md"
                  buttonType="secondaryGray"
                  onClick={onRequestClose}
                >
                  Cancel
                </Button>
                <Button
                  buttonSize="md"
                  buttonType="primary"
                  onClick={apply}
                >
                  Save
                </Button>
              </>
            }
          />
        }>
        <div className={styles.filters}>
          {
            columnFilters.map(c => (
              <FilterRow
                key={c.id}
                column={availableColumns.find(x => c.id === x.id)!}
                value={c.value}
                operator={columnOperators.find(x => x.id === c.id)?.value!}
                setColumnFilters={setColumnFilters}
                setColumnOperators={setColumnOperators}
              />
            ))
          }
          {
            !!unusedColumns.length &&
            <AddFilter
              availableColumns={availableColumns}
              unusedColumns={unusedColumns}
              setColumnFilters={setColumnFilters}
            />
          }
        </div>
      </SideoutMenu>
    </>
  );
});